import React from 'react';
import { Truck, Home, TrendingUp, TrendingDown } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';

const metrics = [
  {
    value: '32',
    suffix: '%',
    label: 'Fuel Cost Reduction',
    context: 'Logistics company reduced operational costs with AI-powered route optimization',
    icon: Truck,
    trend: TrendingDown,
    accentColor: 'primary',
  },
  {
    value: '30',
    suffix: '%',
    label: 'Higher Closing Rate',
    context: 'Real estate agents increased deal closings with AI-powered CRM',
    icon: Home,
    trend: TrendingUp,
    accentColor: 'primary',
  },
];

export const SuccessMetrics = () => {
  const { elementRef, getRevealStyle } = useScrollReveal();

  return (
    <section className="py-24 bg-[#0a0a0a] relative overflow-hidden">
      {/* Background subtle pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,212,255,0.03),transparent_70%)]" />
      
      <div className="container-professional relative z-10" ref={elementRef} style={getRevealStyle()}>
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-primary font-mono mb-4">
            Proven Results
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Success Metrics
          </h2>
          <p className="text-[rgba(255,255,255,0.5)] max-w-xl mx-auto">
            Real outcomes from real clients. No vanity metrics.
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
          {metrics.map((metric, index) => (
            <div 
              key={index}
              className="group relative bg-[#111111] rounded-2xl border border-[rgba(255,255,255,0.08)] p-8 lg:p-10 hover:border-primary/30 transition-all duration-500"
            >
              {/* Glow effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative z-10">
                {/* Icon Row */}
                <div className="flex items-center justify-between mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <metric.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex items-center gap-1 text-emerald-500">
                    <metric.trend className="w-4 h-4" />
                  </div>
                </div>

                {/* Big Number */}
                <div className="mb-4">
                  <span className="text-6xl lg:text-7xl font-bold text-white tracking-tight">
                    {metric.value}
                  </span>
                  <span className="text-4xl lg:text-5xl font-bold text-primary">
                    {metric.suffix}
                  </span>
                </div>

                {/* Label */}
                <h3 className="text-xl font-semibold text-white mb-2">
                  {metric.label}
                </h3>

                {/* Context */}
                <p className="text-[rgba(255,255,255,0.5)] text-sm leading-relaxed">
                  {metric.context}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Speed Metric - Additional Proof */}
        <div className="mt-12 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-6 px-6 sm:px-8 py-5 sm:py-4 rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#111111] hover:border-primary/20 transition-colors duration-300">
            <div className="text-center sm:text-left">
              <span className="text-2xl sm:text-3xl font-bold text-white">3-6</span>
              <span className="text-lg sm:text-xl text-primary font-medium ml-1">months</span>
              <p className="text-[rgba(255,255,255,0.5)] text-xs mt-1">MVP to launch</p>
            </div>
            <div className="hidden sm:block w-px h-12 bg-[rgba(255,255,255,0.1)]" />
            <div className="w-16 h-px sm:hidden bg-[rgba(255,255,255,0.1)]" />
            <div className="text-center sm:text-left">
              <span className="text-2xl sm:text-3xl font-bold text-white">60</span>
              <span className="text-lg sm:text-xl text-primary font-medium ml-1">%</span>
              <p className="text-[rgba(255,255,255,0.5)] text-xs mt-1">Less technical debt</p>
            </div>
            <div className="hidden sm:block w-px h-12 bg-[rgba(255,255,255,0.1)]" />
            <div className="w-16 h-px sm:hidden bg-[rgba(255,255,255,0.1)]" />
            <div className="text-center sm:text-left">
              <span className="text-2xl sm:text-3xl font-bold text-white">15+</span>
              <p className="text-[rgba(255,255,255,0.5)] text-xs mt-1">Clients served</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
